/**
 * Phase 1: Step-based task helpers
 * 
 * Shared helpers for managing task steps (single source of truth)
 * Supports both legacy taskKinds (single, before_after, expiration) and new custom taskKind
 */

export type StepKey = string;

export interface StepSlot {
  role: StepKey;
  prompt?: string;
  required?: boolean;
}

/**
 * Get task step slots from task.submissionSpec.mediaSlots or legacy defaults
 * @param task Task object
 * @returns Array of step slots
 */
export function getTaskStepSlots(task: any): StepSlot[] {
  // If task has submissionSpec.mediaSlots, use that (custom or explicitly set)
  if (task?.submissionSpec?.mediaSlots && Array.isArray(task.submissionSpec.mediaSlots) && task.submissionSpec.mediaSlots.length > 0) {
    return task.submissionSpec.mediaSlots.map((slot: any) => ({
      role: slot.role || 'primary',
      prompt: slot.prompt,
      required: slot.required !== undefined ? slot.required : true
    }));
  }
  
  // Legacy defaults based on taskKind
  const taskKind = task?.taskKind || 'single';
  
  if (taskKind === 'expiration') {
    return [
      { role: 'primary', prompt: 'Front label photo', required: true },
      { role: 'expiration_label', prompt: 'Expiration date close-up', required: true }
    ];
  }
  
  if (taskKind === 'before_after') {
    return [
      { role: 'primary', prompt: 'Before photo', required: true },
      { role: 'after', prompt: 'After photo', required: true }
    ];
  }
  
  // Default: single
  return [
    { role: 'primary', prompt: 'Primary photo', required: true }
  ];
}

/**
 * Get task step keys (roles) from step slots
 * @param task Task object
 * @returns Array of step keys
 */
export function getTaskStepKeys(task: any): StepKey[] {
  return getTaskStepSlots(task).map(slot => slot.role);
}

/**
 * Get display label for a step
 * Priority:
 *   a) task.submissionSteps[stepKey].title
 *   b) slot.prompt
 *   c) legacy human labels for known roles (primary/after/expiration_label)
 *   d) fallback: "Step {index + 1}"
 * @param task Task object
 * @param stepKey Step key (role)
 * @param index Step index (0-based)
 * @returns Display label
 */
export function getStepDisplayLabel(task: any, stepKey: StepKey, index: number): string {
  // Priority a: Check task.submissionSteps[stepKey].title
  if (task?.submissionSteps && typeof task.submissionSteps === 'object') {
    const step = task.submissionSteps[stepKey];
    if (step?.title) {
      return step.title;
    }
  }
  
  // Priority b: Check slot.prompt
  const stepSlots = getTaskStepSlots(task);
  const slot = stepSlots.find(s => s.role === stepKey);
  if (slot?.prompt) {
    return slot.prompt;
  }
  
  // Priority c: Legacy human labels for known roles
  if (stepKey === 'primary') {
    const taskKind = task?.taskKind;
    if (taskKind === 'before_after') {
      return 'Before';
    }
    return 'Primary';
  }
  if (stepKey === 'expiration_label') {
    return 'Expiration Label';
  }
  if (stepKey === 'after') {
    return 'After';
  }
  
  // Priority d: Fallback to "Step {index + 1}"
  return `Step ${index + 1}`;
}



